// @flow strict
import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import classNames from 'classnames';
import styles from './Constellation.module.scss';
import {
  NODES, LINKS, PORTRAIT_LINKS, LINK_STROKE, STAR_PATH, CLUSTERS, NAV, NEIGHBOURS,
  BACKDROP_PATHS, BACKDROP_DOTS, PORTRAIT, PORTRAIT_DESC_ABOVE, PORTRAIT_VIEWBOX
} from './constellation-data';

// How far the whole field slides against the pointer, in px.
const PARALLAX_AMPLITUDE = 16;

type Props = {
  heading: string,
  intro: string
};

const Constellation = ({ heading, intro }: Props) => {
  const [hover, setHover] = useState(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const onMove = (event) => {
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      setOffset({
        x: -((event.clientX / w) - 0.5) * 2 * PARALLAX_AMPLITUDE,
        y: -((event.clientY / h) - 0.5) * 2 * PARALLAX_AMPLITUDE
      });
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  // Resolves one node's appearance for the current selection: the active node
  // grows and takes the accent, its direct neighbours lift slightly, and
  // everything else recedes.
  const stateFor = (node) => {
    const isOn = hover === node.key;
    const isNear = !!hover && !isOn && (NEIGHBOURS[node.key] || []).indexOf(hover) !== -1;
    const resting = node.tier === 3 ? 0.92 : 1;

    let star = 'var(--cn-node)';
    if (isOn) star = 'var(--cn-accent)';
    else if (isNear) star = 'var(--cn-node-near)';

    let label = `var(--cn-tier${node.tier})`;
    if (isOn) label = 'var(--cn-label-on)';
    else if (isNear) label = 'var(--cn-node-near)';

    return {
      isOn,
      scale: isOn ? 1.45 : (isNear ? 1.16 : resting),
      groupOpacity: !hover || isOn || isNear ? 1 : 0.87,
      star,
      label
    };
  };

  const strokeFor = (link) => {
    if (hover && (link.from === hover || link.to === hover)) return 'var(--cn-accent)';
    return `var(--cn-link-${link.weight})`;
  };

  const pointerIsCoarse = () => window.matchMedia('(hover: none)').matches;

  // Touch browsers synthesise mouseenter and focus from a tap, before the
  // click. Left alone those would mark the node selected and the tap below
  // would navigate immediately, so on a coarse pointer selection comes from
  // the click alone.
  const onPoint = (key) => {
    if (pointerIsCoarse()) return;
    setHover(key);
  };

  // With no hover available, the first tap stands in for it — revealing the
  // caption and lighting the node's edges — and a second tap on the same node
  // follows the link.
  const onNodeClick = (event, key) => {
    if (pointerIsCoarse() && hover !== key) {
      event.preventDefault();
      setHover(key);
    }
  };

  const renderLinks = (links, viewBox, className) => (
    <svg
      className={classNames(styles['constellation__links'], className)}
      viewBox={viewBox}
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <g className={styles['constellation__links-group']}>
        {links.map((link) => (
          <path
            key={link.key}
            d={link.d}
            strokeWidth={LINK_STROKE[link.weight]}
            style={{ stroke: strokeFor(link) }}
          />
        ))}
      </g>
    </svg>
  );

  return (
    <div className={styles['constellation']}>
      <header className={styles['constellation__header']}>
        <Link className={styles['constellation__wordmark']} to="/constellation">Mrunal</Link>
        <nav className={styles['constellation__nav']}>
          {NAV.map((item) => (
            <Link key={item.label} className={styles['constellation__nav-link']} to={item.to}>
              {item.label}
            </Link>
          ))}
        </nav>
      </header>

      <section className={styles['constellation__hero']}>
        <div className={styles['constellation__stage']}>

          {/* Sits in the hole at the centre of the field on desktop; stacks
              above it in portrait, where that space is occupied by nodes. */}
          <div
            className={styles['constellation__center']}
            style={{ opacity: hover ? 0.62 : 1 }}
          >
            <h1 className={styles['constellation__title']}>{heading}</h1>
            <p className={styles['constellation__intro']}>{intro}</p>
          </div>

          <svg
            className={styles['constellation__backdrop']}
            viewBox="0 0 1000 680"
            preserveAspectRatio="none"
            aria-hidden="true"
            focusable="false"
          >
            <g className={styles['constellation__backdrop-group']}>
              {BACKDROP_PATHS.map((d) => <path key={d} d={d} />)}
              {BACKDROP_DOTS.map((dot) => (
                <circle key={`${dot.cx},${dot.cy}`} cx={dot.cx} cy={dot.cy} r={dot.r} />
              ))}
            </g>
          </svg>

          {/* Everything that reacts to the pointer rides on this layer. */}
          <div
            className={styles['constellation__field']}
            style={{ transform: `translate3d(${offset.x.toFixed(1)}px, ${offset.y.toFixed(1)}px, 0)` }}
          >
            {renderLinks(LINKS, '0 0 1000 680', styles['constellation__links--wide'])}
            {renderLinks(
              PORTRAIT_LINKS,
              `0 0 ${PORTRAIT_VIEWBOX.w} ${PORTRAIT_VIEWBOX.h}`,
              styles['constellation__links--portrait']
            )}

            {CLUSTERS.map((cluster) => (
              <span
                key={cluster.key}
                className={styles['constellation__cluster']}
                style={{
                  left: cluster.left,
                  top: cluster.top,
                  color: hover && cluster.members.indexOf(hover) !== -1
                    ? 'var(--cn-cluster-on)'
                    : 'var(--cn-cluster)'
                }}
              >
                {cluster.label}
              </span>
            ))}

            {NODES.map((node) => {
              const view = stateFor(node);
              const portrait = PORTRAIT[node.key];

              return (
                <div
                  key={node.key}
                  className={styles['constellation__drift']}
                  // Both layouts' coordinates travel as custom properties so a
                  // media query can pick between them; an inline `left` could
                  // not be overridden.
                  style={{
                    '--nx': node.left,
                    '--ny': node.top,
                    '--px': `${portrait.x}%`,
                    '--py': `${portrait.y}%`,
                    animationName: node.drift,
                    animationDuration: node.duration,
                    animationDelay: node.delay
                  }}
                >
                  <Link
                    to={node.to}
                    className={classNames(
                      styles['constellation__node'],
                      styles[`constellation__node--tier${node.tier}`]
                    )}
                    style={{ opacity: view.groupOpacity }}
                    onMouseEnter={() => onPoint(node.key)}
                    onMouseLeave={() => onPoint(null)}
                    onFocus={() => onPoint(node.key)}
                    onBlur={() => onPoint(null)}
                    onClick={(event) => onNodeClick(event, node.key)}
                  >
                    <svg
                      className={styles['constellation__star']}
                      viewBox="0 0 24 24"
                      style={{ transform: `scale(${view.scale})` }}
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path d={STAR_PATH} style={{ fill: view.star }} />
                    </svg>

                    <span
                      className={styles['constellation__label']}
                      style={{ color: view.label }}
                    >
                      {node.label}
                    </span>

                    <span
                      className={classNames(styles['constellation__desc'], {
                        [styles['constellation__desc--above']]: node.descAbove,
                        [styles['constellation__desc--above-portrait']]:
                          PORTRAIT_DESC_ABOVE.indexOf(node.key) !== -1
                      })}
                      style={{
                        opacity: view.isOn ? 1 : 0,
                        transform: `translateY(${view.isOn ? 0 : 6}px)`
                      }}
                    >
                      {node.desc}
                    </span>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Constellation;
